import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useAuth } from '@/auth/auth-provider'
import { apiPost } from '@/lib/api'
import { createFirstLoginReminderCoordinator, type FirstLoginReminderResponse } from '@/lib/task-reminders'

const reminders = createFirstLoginReminderCoordinator(() =>
  apiPost<FirstLoginReminderResponse>('/task-reminders/first-login'),
)

export function FirstLoginTaskReminder() {
  const { access, loading } = useAuth()
  const userId = access?.id
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  useEffect(() => {
    if (loading || !userId) return
    const currentUserId = userId
    let active = true
    let toastId: string | number | undefined

    async function check() {
      if (!active || document.visibilityState !== 'visible') return
      try {
        const result = await reminders.check(currentUserId)
        if (!active) return
        if (result.created) void queryClient.invalidateQueries({ queryKey: ['notifications'] })
        const notification = reminders.takeNotification(currentUserId, result)
        if (notification) {
          toastId = toast.info(notification.title, {
            id: `task-reminder:${notification.id}`,
            description: notification.body,
            duration: 12000,
            action: {
              label: 'Open tasks',
              onClick: () => navigate(notification.link || '/tasks'),
            },
          })
        }
      } catch {
        // Reminders must never block sign-in. A later focus or visible reentry
        // retries after transient errors, including across local midnight.
      }
    }

    void check()
    window.addEventListener('focus', check)
    document.addEventListener('visibilitychange', check)
    return () => {
      active = false
      window.removeEventListener('focus', check)
      document.removeEventListener('visibilitychange', check)
      if (toastId !== undefined) toast.dismiss(toastId)
    }
  }, [loading, userId, queryClient, navigate])

  return null
}
